import { useState, useEffect, useCallback } from 'react'
import type { Me } from '../lib/types'
import { useTab, type Tab } from '../hooks/useTab'
import { useFamilyData } from '../hooks/useFamilyData'
import { api } from '../lib/api'
import CheckinPage from './CheckinPage'
import DataPage from './DataPage'
import MePage from './MePage'
import AiSummaryPanel from './AiSummaryPanel'
import { isStandalone } from '../lib/env'

interface Props {
  me: Me
  setMe: (me: Me) => void
  onLogout: () => void
}

export default function MainScreen({ me, setMe, onLogout }: Props) {
  const { tab, setTab } = useTab()
  const [showSummary, setShowSummary] = useState(false)
  const fd = useFamilyData()

  const refreshMe = useCallback(async () => {
    try {
      const fresh = await api.me()
      if (fresh) {
        setMe(fresh)
        await fd.refreshMe()
      }
    } catch {}
  }, [setMe, fd])

  useEffect(() => {
    const onFocus = () => {
      fd.refresh()
    }
    window.addEventListener('focus', onFocus)
    const onAiSummary = () => setShowSummary(true)
    window.addEventListener('open-ai-summary', onAiSummary)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('open-ai-summary', onAiSummary)
    }
  }, [fd])

  const aiTextEnabled = me.ai_configured
  const aiVisionEnabled = me.ai_vision_configured
  const photosEnabled = me.photos_enabled

  return (
    <div className="main-screen">
      <CompactHeader me={me} onLogout={onLogout} onFamilyChange={refreshMe} refreshing={fd.refreshing} />

      <div className="main-content">
        {tab === 'checkin' && (
          <CheckinPage onRecordChange={() => fd.refresh()} photosEnabled={photosEnabled} />
        )}
        {tab === 'data' && (
          <DataPage
            aiTextEnabled={aiTextEnabled}
            aiVisionEnabled={aiVisionEnabled}
          />
        )}
        {tab === 'me' && <MePage me={me} onLogout={onLogout} onFamilyChange={refreshMe} />}
      </div>

      <BottomNav current={tab} onChange={setTab} />

      {showSummary && (
        <AiSummaryPanel onClose={() => setShowSummary(false)} onChange={() => fd.refresh()} />
      )}
    </div>
  )
}

function CompactHeader({ me, onLogout, onFamilyChange, refreshing }: { me: Me; onLogout: () => void; onFamilyChange: () => void; refreshing: boolean }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const isOwner = me.device.role === 'owner'

  return (
    <header className="compact-header">
      <div className="compact-header-left">
        <span className="compact-header-avatar">👶</span>
        <div className="compact-header-info">
          <div className="compact-header-name">{me.device.name}</div>
          <div className="compact-header-role">
            {isOwner ? '管理员' : '成员'} · {me.family.name}
          </div>
        </div>
      </div>
      <div className="compact-header-right">
        {refreshing && <span className="compact-header-refreshing">同步中</span>}
        <button
          className="compact-header-menu-btn"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="菜单"
        >
          ⋯
        </button>
        {menuOpen && (
          <div className="compact-header-menu" onClick={() => setMenuOpen(false)}>
            <button
              onClick={() => {
                onFamilyChange()
                setMenuOpen(false)
              }}
            >
              🔄 刷新数据
            </button>
            <button
              onClick={() => {
                if (confirm('确定退出当前家庭？')) onLogout()
              }}
            >
              退出家庭
            </button>
          </div>
        )}
      </div>
    </header>
  )
}

interface NavProps {
  current: Tab
  onChange: (t: Tab) => void
}

function BottomNav({ current, onChange }: NavProps) {
  const items: { key: Tab; label: string; icon: string; center?: boolean }[] = [
    { key: 'data', label: '数据', icon: '📊' },
    { key: 'checkin', label: '打卡', icon: '✏️', center: true },
    { key: 'me', label: '我的', icon: '👤' },
  ]
  return (
    <nav className="bottom-nav">
      {items.map((it) => {
        const active = current === it.key
        if (it.center) {
          return (
            <button
              key={it.key}
              className={`nav-btn center ${active ? 'active' : ''}`}
              onClick={() => onChange(it.key)}
              aria-label={it.label}
            >
              <span className="nav-center-icon">{it.icon}</span>
              <span className="nav-center-label">{it.label}</span>
            </button>
          )
        }
        return (
          <button
            key={it.key}
            className={`nav-btn ${active ? 'active' : ''}`}
            onClick={() => onChange(it.key)}
            aria-label={it.label}
          >
            <span className="nav-icon">{it.icon}</span>
            <span className="nav-label">{it.label}</span>
          </button>
        )
      })}
    </nav>
  )
}
